<template>
    <grid
        ref="grid"
        v-if="account"
        :column-defs="columnDefs"
        :row-data="doors"
        :is-auto-height="false"
    >
        <responsive-button
            :icon="['fas', 'user']"
            :clickable="false"
            :responsiveness="false"
            variant="link"
        >{{ accountNameTruncated }}</responsive-button>
        <div class="break d-sm-block d-md-none" />
        <responsive-button
            :icon="['fas', 'arrows-rotate']"
            @click="handleSyncDoorsClick"
            variant="primary"
        >Fetch Kaba doors</responsive-button>
        <responsive-button
            :icon="['fas', 'gear']"
            @click="handleSettingsClick"
            variant="primary"
        >Settings</responsive-button>
        <responsive-button
            :icon="['fas', 'lock']"
            @click="handleUpdatePasswordClick"
            variant="primary"
        >Update password</responsive-button>
        <responsive-button
            :icon="['fas', 'right-from-bracket']"
            @click="handleDisconnectClick"
            variant="primary"
        >Disconnect</responsive-button>
        <responsive-button
            :icon="['fas', 'plus']"
            @click="handleFetchLodgixPropertiesClick"
            variant="primary"
        >Fetch Lodgix properties</responsive-button>
        <settings-modal-form
            :modal-id="settingsModalID"
        />
        <disconnect-modal
            title="Disconnect Kaba account"
            description="Are you sure you want to disconnect your Kaba account? You will not be able to generate lock codes for your reservations any more."
            :modal-id="disconnectModalID"
            :disconnect="disconnect"
            @success-disconnect="load"
        />
        <fetch-lodgix-properties-modal
            account="Kaba"
            :modal-id="fetchLodgixPropertiesModalID"
            :key="fetchLodgixPropertiesModalKey"
            :process="createDoor"
            @success-save="load"
        />
        <update-password-modal
            title="Update Kaba password"
            :modal-id="updatePasswordModalID"
            :updatePassword="updatePassword"
            @success-save="load"
        />
    </grid>
    <div v-else-if="busy"></div>
    <div class="mt-1 ml-2" v-else>
        <b-col>
            <b-row>
                <responsive-button
                    :icon="['fas', 'right-to-bracket']"
                    :responsiveness="false"
                    @click="handleLoginClick"
                    variant="primary"
                >Login to Kaba</responsive-button>
            </b-row>
        </b-col>
        <kaba-login-modal-form
            :modal-id="loginModalID"
            @success-save="handleLoginSuccess"
        />
    </div>
</template>

<script>
    import API from '../../../../api';
    import {
        DOOR_LOCKS_KABA_ACCOUNT_ENDPOINT,
        DOOR_LOCKS_KABA_ACCOUNT_DISCONNECT_ENDPOINT,
        DOOR_LOCKS_KABA_ACCOUNT_PASSWORD_ENDPOINT,
        DOOR_LOCKS_KABA_DOORS_ENDPOINT,
        DOOR_LOCKS_KABA_DOORS_FETCH_ENDPOINT,
        DOOR_LOCKS_KABA_DOOR_BY_ID_ENDPOINT,
        DOOR_LOCKS_KABA_DOOR_PROPERTIES_ENDPOINT,
        DOOR_LOCKS_KABA_DOOR_USERS_ENDPOINT,
    } from '../../../../endpoints';
    import { truncate } from '../../../../utils/strings';
    import Grid from '../../../generic/grid/grid.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import DisconnectModal from '../common/disconnect-modal.vue';
    import FetchLodgixPropertiesModal from '../common/fetch-lodgix-properties-modal.vue';
    import SettingsModalForm from '../common/settings-modal-form.vue';
    import UpdatePasswordModal from '../common/update-password-modal.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import KabaLoginModalForm from './kaba-login-modal-form.vue';

    export default {
        name: 'KabaSettings',
        components: {
            DisconnectModal,
            FetchLodgixPropertiesModal,
            Grid,
            ResponsiveButton,
            KabaLoginModalForm,
            SettingsModalForm,
            UpdatePasswordModal,
            actionsCellRenderer: ActionsCellRenderer,
        },
        mounted() {
            this.load();
        },
        data() {
            return {
                busy: false,
                account: null,
                disconnectModalID: 'kaba-disconnect-modal',
                fetchLodgixPropertiesModalID: 'kaba-fetch-lodgix-properties-modal',
                fetchLodgixPropertiesModalKey: 0,
                loginModalID: 'kaba-login-modal',
                settingsModalID: 'kaba-settings-modal',
                updatePasswordModalID: 'kaba-update-password-modal',
                doors: [],
                propertyOptions: [],
                userOptions: [],
                columnDefs: [
                    {
                        field: 'name',
                        headerName: 'Kaba Door',
                        sort: 'asc',
                    },
                    {
                        headerName: 'Default Kaba User',
                        cellEditor: 'selectCellEditor',
                        editable: true,
                        cellClass: 'text-primary lx-editable-cell',
                        cellEditorParams: ({ data }) => ({
                            options: async () => {
                                try {
                                    const response = await API.get(DOOR_LOCKS_KABA_DOOR_USERS_ENDPOINT(data.id));
                                    this.userOptions = response.data.map(user => ({
                                        value: user.id,
                                        text: user.name,
                                    }));
                                } catch {
                                    this.errorToast({text: 'Could not load user options'});
                                }
                                return this.userOptions;
                            }
                        }),
                        valueGetter: ({ data }) => data.kaba_profileuser_id,
                        getQuickFilterText: ({ data }) => data.kaba_profileuser_name,
                        cellRenderer: ({ data }) => this.getUserName({ data }),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            const oldUserName = data.kaba_profileuser_name;
                            (async () => {
                                try {
                                    await API.put(
                                        DOOR_LOCKS_KABA_DOOR_BY_ID_ENDPOINT(data.id),
                                        {
                                            kaba_profileuser_id: newValue,
                                            rental_property_id: data.rental_property_id,
                                        }
                                    );
                                } catch {
                                    this.errorToast({text: 'Could not save user'})
                                    data.kaba_profileuser_id = oldValue;
                                    data.kaba_profileuser_name = oldUserName;
                                    node.setData(data)
                                }
                            })();
                            const user = this.userOptions.find(p => p.value === newValue);
                            data.kaba_profileuser_id = newValue;
                            data.kaba_profileuser_name = user.text;
                            return true;
                        },
                    },
                    {
                        headerName: 'Lodgix Property',
                        cellEditor: 'selectCellEditor',
                        editable: true,
                        cellClass: 'text-primary lx-editable-cell',
                        cellEditorParams: ({ data }) => ({
                            options: async () => {
                                this.propertyOptions = [{ value: null, text: 'Not set'}];
                                try {
                                    const response = await API.get(DOOR_LOCKS_KABA_DOOR_PROPERTIES_ENDPOINT(data.id));
                                    this.propertyOptions.push(
                                        ...response.data.map(property => ({
                                            value: property.id,
                                            text: property.name,
                                        }))
                                    );
                                } catch {
                                    this.errorToast({text: 'Could not load property options.'}) 
                                }
                                return this.propertyOptions;
                            }
                        }),
                        valueGetter: ({ data }) => data.rental_property_id,
                        getQuickFilterText: ({ data }) => data.rental_property_name,
                        cellRenderer: ({ data }) => this.getPropertyName({ data }),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            const oldPropertyName = data.rental_property_name;
                            (async () => {
                                try {
                                    await API.put(
                                        DOOR_LOCKS_KABA_DOOR_BY_ID_ENDPOINT(data.id),
                                        {
                                            kaba_profileuser_id: data.kaba_profileuser_id,
                                            rental_property_id: newValue,
                                        }
                                    );
                                } catch {
                                    this.errorToast({text: 'Could not save property'}) 
                                    data.rental_property_id = oldValue;
                                    data.rental_property_name = oldPropertyName;
                                    node.setData(data)
                                }
                            })();
                            const property = this.propertyOptions.find(p => p.value === newValue);
                            data.rental_property_id = newValue;
                            data.rental_property_name = property.text;
                            return true;
                        },
                    },
                    {
                        headerName: '',
                        maxWidth: 80,
                        minWidth: 80,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            delete: async (door, data) => {
                                try {
                                    await API.delete(DOOR_LOCKS_KABA_DOOR_BY_ID_ENDPOINT(door.id), { data });
                                } catch (error) {
                                    this.errorToast({text: 'Could not delete door'}) 
                                    return;
                                }
                                this.refreshDoors();
                            },
                        }
                    },
                ],
            }
        },
        computed: {
            accountNameTruncated() {
                if (this.account && this.account.site_name && this.account.login_name) {
                    return truncate(`${this.account.site_name} - ${this.account.login_name}`, 20);
                }
                return '';
            }
        },
        methods: {
            getPropertyName({ data }) {
                if (data) {
                    if (data.rental_property_id) {
                        return data.rental_property_name || 'No name';
                    }
                }
                return 'Not set';
            },
            getUserName({ data }) {
                if (data) {
                    if (data.kaba_profileuser_id) {
                        return data.kaba_profileuser_name || 'No name';
                    }
                }
                return 'Not set';
            },
            handleDisconnectClick() {
                this.$bvModal.show(this.disconnectModalID);
            },
            handleSettingsClick() {
                this.$bvModal.show(this.settingsModalID);
            },
            handleLoginClick() {
                this.$bvModal.show(this.loginModalID);
            },
            handleUpdatePasswordClick() {
                this.$bvModal.show(this.updatePasswordModalID);
            },
            handleFetchLodgixPropertiesClick() {
                this.fetchLodgixPropertiesModalKey += 1;
                this.$nextTick(() => {
                    this.$bvModal.show(this.fetchLodgixPropertiesModalID);
                })
            },
            async handleSyncDoorsClick() {
                this.busy = true;
                this.$parent.$emit('busy', true);

                try {
                    await this.syncDoors();
                    await this.fetchDoors();
                } finally {
                    this.busy = false;
                    this.$parent.$emit('busy', false);
                }
            },
            handleLoginSuccess() {
                return this.load({ withSync: true });
            },
            createDoor({ id }) {
                return API.post(DOOR_LOCKS_KABA_DOORS_ENDPOINT(), { id });
            },
            disconnect() {
                return API.post(DOOR_LOCKS_KABA_ACCOUNT_DISCONNECT_ENDPOINT());
            },
            updatePassword(values) {
                return API.post(DOOR_LOCKS_KABA_ACCOUNT_PASSWORD_ENDPOINT(), values);
            },
            async load({ withSync = false } = {}) {
                this.busy = true;
                this.$parent.$emit('busy', true);

                try {
                    await this.fetchAccount();
                    if (withSync) {
                        this.successToast({text: `Fetching doors...`});
                        await this.syncDoors();
                    }
                    if (this.account) {
                        await this.fetchDoors();
                    } else {
                        this.doors = [];
                    }
                } finally {
                    this.busy = false;
                    this.$parent.$emit('busy', false);
                }
            },
            async fetchAccount() {
                try {
                    const response = await API.get(DOOR_LOCKS_KABA_ACCOUNT_ENDPOINT());
                    this.account = response.data;
                } catch {
                    this.account = null;
                }
            },
            async fetchDoors() {
                try {
                    const response = await API.get(DOOR_LOCKS_KABA_DOORS_ENDPOINT());
                    this.doors = response.data;
                } catch {
                   this.errorToast({text: `Could not load doors`});
                }
            },
            async syncDoors() {
                try {
                    const response = await API.get(DOOR_LOCKS_KABA_DOORS_FETCH_ENDPOINT());
                    const count = response.data.count;
                    this.successToast({text: `Fetched ${count} ${count === 1 ? 'door' : 'doors'}.`})
                } catch {
                    this.errorToast({text: 'Could not load doors'})
                }
            },
            async refreshDoors() {
                this.$parent.$emit('busy', true);

                try {
                    await this.fetchDoors();
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
        }
    }
</script>

<style scoped lang="scss">
    .break {
        flex-basis: 100%;
        height: 0;
    }
</style>
